// ポータル自身のログイン（oidc-client-ts）。
//
// authorization_endpoint を自分の共通ログイン画面（/login）に向ける（ADR-0016）。ライブラリが
// authorize 要求を /login へ送り、/login が email のドメインで振り分けて Cognito へ転送する。
// token_endpoint / jwks は Cognito のまま。

import { UserManager, WebStorageStateStore, type User } from "oidc-client-ts";
import { getConfig } from "./config";

let manager: UserManager | null = null;

export function userManager(): UserManager {
  if (manager) return manager;
  const c = getConfig();
  const origin = window.location.origin;
  manager = new UserManager({
    authority: `https://cognito-idp.${regionOf(c.userPoolId)}.amazonaws.com/${c.userPoolId}`,
    client_id: c.clientId,
    redirect_uri: `${origin}/auth/callback`,
    post_logout_redirect_uri: `${origin}/login`,
    response_type: "code",
    scope: "openid email profile",
    metadata: {
      issuer: `https://cognito-idp.${regionOf(c.userPoolId)}.amazonaws.com/${c.userPoolId}`,
      // 共通ログイン画面が認可エンドポイントの入口（ADR-0016）。
      authorization_endpoint: `${origin}/login`,
      token_endpoint: `${c.cognitoDomain}/oauth2/token`,
      userinfo_endpoint: `${c.cognitoDomain}/oauth2/userInfo`,
      end_session_endpoint: `${c.cognitoDomain}/logout`,
      revocation_endpoint: `${c.cognitoDomain}/oauth2/revoke`,
      jwks_uri: `https://cognito-idp.${regionOf(c.userPoolId)}.amazonaws.com/${c.userPoolId}/.well-known/jwks.json`,
    },
    userStore: new WebStorageStateStore({ store: window.sessionStorage }),
    automaticSilentRenew: false,
    // Cognito の /logout は client_id と logout_uri を要求する。
    extraQueryParams: {},
  });
  return manager;
}

const regionOf = (userPoolId: string) => userPoolId.split("_")[0];

export async function currentUser(): Promise<User | null> {
  const u = await userManager().getUser();
  return u && !u.expired ? u : null;
}

/** ログインの往復で持ち回る値（signinCallback の User.state で戻る）。 */
export interface SignInState {
  /** 招待のリンクから来たときの、招待した契約。 */
  invitedContractId?: string;
}

/**
 * ポータル自身のログインを始める。招待のリンクから来たとき（invitedContractId あり）は再認証を求める
 * （prompt=login）。Cognito のセッションが残っていると認証が起きず、Post Authentication が招待を有効化しないため。
 */
export async function signIn(loginHint?: string, state?: SignInState): Promise<void> {
  await userManager().signinRedirect({
    ...(loginHint ? { login_hint: loginHint } : {}),
    ...(state?.invitedContractId ? { prompt: "login", state } : {}),
  });
}

/**
 * ポータル自身のセッションを終える（refresh token の失効とトークンの破棄）。Cognito の /logout は呼ばない。
 * 共通ログイン画面が authorize 要求なしで開かれたとき（サービス・ポータルのログアウト後の着地など）に使う
 * （portal.md「共通ログイン画面」/ ADR-0026）。
 */
export async function endSession(): Promise<void> {
  const m = userManager();
  const u = await m.getUser().catch(() => null);
  if (u?.refresh_token) await m.revokeTokens(["refresh_token"]).catch(() => { /* 失効できなくてもトークンは捨てる */ });
  await m.removeUser();
}

export async function signOut(): Promise<void> {
  const c = getConfig();
  await endSession();
  const u = new URL(`${c.cognitoDomain}/logout`);
  u.searchParams.set("client_id", c.clientId);
  u.searchParams.set("logout_uri", `${window.location.origin}/login`);
  window.location.assign(u.toString());
}
